import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DateInputFillMode, DateInputRounded } from '@progress/kendo-angular-dateinputs';
import { CheckBoxRounded, InputSize } from '@progress/kendo-angular-inputs';
import { DictionaryAPIService } from 'src/app/extensions/services/api/dictionary-api.service';
import { isEqual } from 'lodash';

@Component({
  selector: 'emails-list-filter',
  templateUrl: './emails-list-filter.component.html',
  styleUrls: ['./emails-list-filter.component.scss'],
  standalone: false,
})
export class EmailsListFilterComponent implements OnInit {
  @Input() public pageFilter: app.models.EmailXRequestTypes;

  public constructor(
    private _activeModal: NgbActiveModal,
    private _dictionaryAPIService: DictionaryAPIService
  ) {}

  public filterText: string;

  public fillModeOutline: DateInputFillMode = 'outline';
  public roundedLarge: DateInputRounded = 'large';

  public size: InputSize = 'large';
  public rounded: CheckBoxRounded = 'large';

  noDataTemplate: string;

  allAssetDocumentRequestTypes: boolean = false;
  allContractModificationRequestTypes: boolean = false;
  allClientDataRequestTypes: boolean = false;

  dictionaryAssetDocumentRequestTypes: app.models.DictionaryDataModel[];
  dictionaryContractModificationRequestTypes: app.models.DictionaryDataModel[];
  dictionaryClientDataRequestTypes: app.models.DictionaryDataModel[];

  selectedDictionaryAssetDocumentRequestTypeIds: string[];
  selectedDictionaryClientDataRequestTypeIds: string[];
  selectedDictionaryContractModificationRequestTypeIds: string[];

  // Track checkbox states (mapping requestType.id to boolean)
  assetDocumentRequestTypeCheckboxStates: { [id: string]: boolean } = {};
  contractModificationRequestTypeCheckboxStates: { [id: string]: boolean } = {};
  clientDataRequestTypeCheckboxStates: { [id: string]: boolean } = {};
  initialFilterState: app.models.EmailXRequestTypes;

  public ngOnInit(): void {
    if (this.pageFilter.dictionaryAssetDocumentRequestTypeIds) {
      this.selectedDictionaryAssetDocumentRequestTypeIds = structuredClone(
        this.pageFilter.dictionaryAssetDocumentRequestTypeIds
      );
      this.selectedDictionaryAssetDocumentRequestTypeIds.forEach(id => {
        this.assetDocumentRequestTypeCheckboxStates[id] = true;
      });
    }
    if (this.pageFilter.dictionaryClientDataRequestTypeIds) {
      this.selectedDictionaryClientDataRequestTypeIds = structuredClone(
        this.pageFilter.dictionaryClientDataRequestTypeIds
      );
      this.selectedDictionaryClientDataRequestTypeIds.forEach(id => {
        this.clientDataRequestTypeCheckboxStates[id] = true;
      });
    }
    if (this.pageFilter.dictionaryContractModificationRequestTypeIds) {
      this.selectedDictionaryContractModificationRequestTypeIds = structuredClone(
        this.pageFilter.dictionaryContractModificationRequestTypeIds
      );

      this.selectedDictionaryContractModificationRequestTypeIds.forEach(id => {
        this.contractModificationRequestTypeCheckboxStates[id] = true;
      });
    }
    this._dictionaryAPIService.getDictionaryAssetDocumentRequestTypes().subscribe(result => {
      result.forEach(x => {
        x.id = x.id.toLowerCase();
      });
      this.dictionaryAssetDocumentRequestTypes = result;
      this.allAssetDocumentRequestTypes = this.syncAllRequestTypes(
        this.dictionaryAssetDocumentRequestTypes,
        this.pageFilter.dictionaryAssetDocumentRequestTypeIds
      );
    });

    this._dictionaryAPIService.getDictionaryContractModificationRequestType().subscribe(result => {
      result.forEach(x => {
        x.id = x.id.toLowerCase();
      });
      this.dictionaryContractModificationRequestTypes = result;
      this.allContractModificationRequestTypes = this.syncAllRequestTypes(
        this.dictionaryContractModificationRequestTypes,
        this.pageFilter.dictionaryContractModificationRequestTypeIds
      );
    });

    this._dictionaryAPIService.getDictionaryClientDataRequestTypes().subscribe(result => {
      result.forEach(x => {
        x.id = x.id.toLowerCase();
      });
      this.dictionaryClientDataRequestTypes = result;
      this.allClientDataRequestTypes = this.syncAllRequestTypes(
        this.dictionaryClientDataRequestTypes,
        this.pageFilter.dictionaryClientDataRequestTypeIds
      );
    });
  }

  public closeHandler() {
    this._activeModal.close(false);
  }

  public resetHandler() {
    this.pageFilter.dictionaryAssetDocumentRequestTypeIds = [];
    this.pageFilter.dictionaryClientDataRequestTypeIds = [];
    this.pageFilter.dictionaryContractModificationRequestTypeIds = [];

    this._activeModal.close(isEqual(this.pageFilter, this.initialFilterState) ? false : 0);
  }

  public applyHandler() {
    let filterCount = 0;
    this.pageFilter.dictionaryAssetDocumentRequestTypeIds = this.selectedDictionaryAssetDocumentRequestTypeIds;
    this.pageFilter.dictionaryClientDataRequestTypeIds = this.selectedDictionaryClientDataRequestTypeIds;
    this.pageFilter.dictionaryContractModificationRequestTypeIds =
      this.selectedDictionaryContractModificationRequestTypeIds;

    filterCount += this.pageFilter.dictionaryAssetDocumentRequestTypeIds.length;
    filterCount += this.pageFilter.dictionaryClientDataRequestTypeIds.length;
    filterCount += this.pageFilter.dictionaryContractModificationRequestTypeIds.length;
    this._activeModal.close(isEqual(this.pageFilter, this.initialFilterState) ? false : filterCount);
  }

  onAllRequestTypeCheckboxChange(
    isChecked: boolean,
    sourceList: app.models.DictionaryDataModel[],
    checkboxStates: { [id: string]: boolean },
    ids: string[]
  ): void {
    sourceList.forEach(requestType => {
      checkboxStates[requestType.id] = isChecked;
      if (isChecked && !ids.includes(requestType.id)) {
        ids.push(requestType.id);
      } else if (!isChecked) {
        const index = ids.indexOf(requestType.id);
        if (index > -1) {
          ids.splice(index, 1);
        }
      }
    });
  }

  syncAllRequestTypes(sourceList: app.models.DictionaryDataModel[], ids: string[]): boolean {
    return sourceList.every(x => ids.some(id => x.id === id));
  }

  onRequestTypeCheckboxChange(
    id: string,
    sourceList: app.models.DictionaryDataModel[],
    checkboxStates: { [id: string]: boolean },
    ids: string[]
  ): boolean {
    if (checkboxStates[id] && !ids.includes(id)) {
      ids.push(id);
    } else if (!checkboxStates[id]) {
      const index = ids.indexOf(id);
      if (index > -1) {
        ids.splice(index, 1);
      }
    }

    return this.syncAllRequestTypes(sourceList, ids);
  }
}
